import { TextField } from "@shared/components/TextField";
import { useFormContext } from "react-hook-form";
import {
  SubscriptionFlowTokenValidationSchema,
  changeEmailTokenMask,
} from "./subscription-flow-change-email-form-helpers";
import { Form } from "@shared/components/Form";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { Button, Typography } from "@mui/material";
import styled from "@emotion/styled";
import MaskedInput from "react-text-mask";

type Props = {
  handleConfirmTokenChangeSubscriptionEmail: (data: {
    token: string;
  }) => Promise<void>;
  status: "idle" | "pending" | "success" | "error";
  onCancel: () => void;
};

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    button {
      flex: 1;
    }
  }
`;

const formatToken = (token: string) => {
  return token.trim().replace(/\s/g, "").replace(/_/g, "");
};

export const SubscriptionFlowTokenValidationForm = (props: Props) => {
  const { handleConfirmTokenChangeSubscriptionEmail, onCancel, status } = props;
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useFormContext<SubscriptionFlowTokenValidationSchema>();

  const onSubmit = async ({ token }: SubscriptionFlowTokenValidationSchema) => {
    await handleConfirmTokenChangeSubscriptionEmail({
      token: formatToken(token),
    });
    reset();
    onCancel();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const token = watch("token");
  const has6Digits = formatToken(token).length >= 6;

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      aria-disabled={status === "pending"}
    >
      <Typography mb={2} variant="body2" color="primary">
        Please provide the 6-digit token sent to your email
      </Typography>
      <MaskedInput
        mask={changeEmailTokenMask}
        render={(ref, props) => (
          <TextField
            InputProps={{ inputRef: ref }}
            {...props}
            label="Token"
            name="token"
            inputProps={{
              ...register("token"),
              style: {
                fontSize: "1.6rem",
                textAlign: "center",
              },
            }}
            fullWidth
            fieldError={errors.token}
            disabled={status === "pending"}
          />
        )}
      />
      <ActionsContainer>
        <Button variant="text" onClick={handleCancel}>
          Cancel
        </Button>
        <SubmitButton isLoading={status === "pending"} disabled={!has6Digits}>
          Submit
        </SubmitButton>
      </ActionsContainer>
    </Form>
  );
};
